import React, { memo, useEffect, useState } from "react";
import { compose } from "redux";
import { connect, useDispatch } from "react-redux";
import { reduxForm } from "redux-form";
import { createStructuredSelector } from "reselect";
import {
  clearData,
  fetchReferralCode,
  setEkycCompleted,
  fetchOrderStatus,
  updateSadOrder,
  updateOMSOrder,
  postPaymentRequest,
  setData
} from "ducks/actions";
import {
  makeSelectIsFetchingData,
  makeSelectOrder,
  makeSelectOrderParams,
  makeSelectPayMonthlyItems,
  makeSelectPayTodayItems,
  makeSelectDeliveryDetailsItems,
  makeSelectReferralCode,
  makeSelectPaymentRequest,
  makeSelectOrderSummaryForm,
  makeSelectOrderSummaryFormInitialValues,
  makeSelectFormattedSelectedNumber,
  makeSelectSelectedAddons,
  makeSelectDefaultPlanAddons,
  makeSelectPlanDetails,
  makeSelectDefaultPromoCodeOffers,
  makeSelectSessionToken,
  makeSelectMultiSimOrder,
  makeSelectSimCount,
  makeSelectSadOrderParams,
  makeSelectValidateIccId,
  makeSelectPersonalDetailsForm,
  makeSelectChannelReqOrderParams,
  makeSelectShowSectionListModal,
  makeSelectAppSettings,
  makeSelectMyInfoIsUsed,
  makeSelectIsWalletPromoCode,
  makeSelectValidateOrderError
} from "ducks/selectors";
import SmartRender from "containers/SmartRender";
import {
  forms,
  O2O_CHANNEL,
  paths,
  SELF_ACT_DEL,
  VERIFY_IDENTITY_CHANNEL
} from "utils/constants";
import {
  pathWithChannel,
  gotoUrl,
  getChannelFromUrl,
  checkOrderParamsIsValid
} from "utils/helpers";
import get from "lodash/get";
import isFunction from "lodash/isFunction";
import {
  makeSelectOrderRef,
  makeSelectRedoEKycParams
} from "ducks/selectors/order";
import { changeFormField } from "ducks/actions/index";

const SelfActivationOrderSummaryHoc = Component =>
  function Comp(props) {
    const {
      setEkycCompleted: callSetEkycCompleted,
      fetchOrder,
      sadOrderParams,
      orderParams,
      token,
      validateIccid,
      redoEKycParams,
      channelRequiredOrderParams,
      formValues,
      myInfoIsUsed,
      isWalletPromoCode,
      appSettings,
      changeField
    } = props;
    const dispatch = useDispatch();
    const [canRequestPayment, setCanRequestPayment] = useState(false);
    const fetchOrderCallback = (result, sessionToken, navigate) => {
      const order_ref = result?.order?.order_ref;
      fetchOrder(order_ref, sessionToken, order => {
        callSetEkycCompleted(sessionToken);
        const orderPaid = get(order, "paid");
        setCanRequestPayment(!orderPaid);
        if (orderPaid) {
          isFunction(navigate)
            ? navigate()
            : gotoUrl(pathWithChannel(paths.SUCCESS));
        }
      });
    };

    const updateOrderAndNavigate = navigate => {
      checkOrderParamsIsValid(redoEKycParams, channelRequiredOrderParams) &&
        dispatch(
          updateOMSOrder({ token, ...redoEKycParams }, (result, error) => {
            !error && fetchOrderCallback(result, token, navigate);
          })
        );
    };

    useEffect(() => {
      if (token) {
        const isVerifyIdentityFlow =
          getChannelFromUrl() === VERIFY_IDENTITY_CHANNEL;
        if (isVerifyIdentityFlow) {
          // Skip auto submit and call updateOrderAndNavigate from Page Navigation
        } else {
          const isSadFlow = get(validateIccid, "channel") === SELF_ACT_DEL;
          if (isSadFlow) {
            checkOrderParamsIsValid(
              sadOrderParams,
              channelRequiredOrderParams
            ) &&
              dispatch(
                updateSadOrder(
                  { token, ...sadOrderParams },
                  (result, error) => {
                    !error && fetchOrderCallback(result, token);
                  }
                )
              );
          } else {
            checkOrderParamsIsValid(orderParams, channelRequiredOrderParams, {
              channel: O2O_CHANNEL
            }) &&
              dispatch(
                updateOMSOrder({ token, ...orderParams }, (result, error) => {
                  !error && fetchOrderCallback(result, token);
                })
              );
          }
        }
      }
      const noUpfrontPayment = get(props, "payTodayItems.totalAmount") < 1;
      const showWallet = noUpfrontPayment && myInfoIsUsed && isWalletPromoCode;
      const defaultWalletPayment =
        showWallet && get(appSettings, "enable_wallet_payment_default");
      const defaultPaymentType = defaultWalletPayment ? "wallet" : "recurring";
      if (formValues?.payment_type !== defaultPaymentType) {
        changeField("payment_type", defaultPaymentType);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <Component
        {...props}
        canRequestPayment={canRequestPayment}
        updateOrderAndNavigate={updateOrderAndNavigate}
      />
    );
  };

const mapStateToProps = createStructuredSelector({
  formValues: makeSelectOrderSummaryForm(),
  order: makeSelectOrder(),
  addons: makeSelectSelectedAddons(),
  defaultPlanAddons: makeSelectDefaultPlanAddons(),
  paymentRequest: makeSelectPaymentRequest(),
  orderParams: makeSelectOrderParams("sim_activation"),
  isCreatingOrder: makeSelectIsFetchingData("order"),
  isFetchingPaymentRequest: makeSelectIsFetchingData("paymentRequest"),
  payMonthlyItems: makeSelectPayMonthlyItems(),
  payTodayItems: makeSelectPayTodayItems("OrderSummary"),
  deliveryDetailsItems: makeSelectDeliveryDetailsItems(),
  referralCode: makeSelectReferralCode(),
  initialValues: makeSelectOrderSummaryFormInitialValues(),
  selectedNumber: makeSelectFormattedSelectedNumber(),
  planDetails: makeSelectPlanDetails(),
  sim_count: makeSelectSimCount(),
  isMultiSimOrder: makeSelectMultiSimOrder(),
  defaultPromoCodeOffers: makeSelectDefaultPromoCodeOffers(),
  token: makeSelectSessionToken(),
  sadOrderParams: makeSelectSadOrderParams(),
  validateIccid: makeSelectValidateIccId(),
  personalDetailFormsValues: makeSelectPersonalDetailsForm(),
  orderRef: makeSelectOrderRef(),
  redoEKycParams: makeSelectRedoEKycParams(),
  channelRequiredOrderParams: makeSelectChannelReqOrderParams(),
  showSectionListModal: makeSelectShowSectionListModal(),
  appSettings: makeSelectAppSettings(),
  myInfoIsUsed: makeSelectMyInfoIsUsed(),
  isWalletPromoCode: makeSelectIsWalletPromoCode(),
  validateOrderError: makeSelectValidateOrderError()
});

export function mapDispatchToProps(dispatch) {
  return {
    clearOrder: () => {
      dispatch(clearData("order"));
    },
    fetchOrder: (orderRef, token, callback) => {
      dispatch(fetchOrderStatus({ order_ref: orderRef, token }, callback));
    },
    fetchPaymentRequest: (params, callback = () => {}) => {
      dispatch(postPaymentRequest(params, callback));
    },
    postPaymentRequest: (params, callback = () => {}) => {
      dispatch(postPaymentRequest(params, callback));
    },
    clearPaymentRequest: () => {
      dispatch(clearData("paymentRequest"));
    },
    setEkycCompleted: token => {
      dispatch(setEkycCompleted({ token, completed: true }));
    },
    validateReferralCode: code => {
      dispatch(fetchReferralCode({ referral_code: code }));
    },
    setSectionListModal: (value = false) => {
      dispatch(
        setData({
          key: "showSectionListModal",
          data: { enabled: value }
        })
      );
    },
    changeField: (field, value) => {
      dispatch(
        changeFormField({
          form: forms.ORDER_SUMMARY_FORM,
          field: field,
          value: value
        })
      );
    },
    resetReferralCode: () =>
      dispatch(clearData(["referralCode", "selectedAddons"]))
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(
  withConnect,
  reduxForm({
    form: forms.ORDER_SUMMARY_FORM,
    destroyOnUnmount: false
  }),
  memo
)(SelfActivationOrderSummaryHoc(SmartRender));
